import { cn } from '@nwwp/ui-shadcn'
import { AdCard } from './AdCard'
import { GaugeSVG } from './GaugeSVG'
import { fmtCurrency, fmtFullCurrency } from '../../../utils/currency-format'
import type { CalculatorResults, CalculatorState } from '../types'

function monthlyCost(total: number, years: number): string {
  return `${fmtFullCurrency(total / years / 12)}/mo effective`
}

function verdict(results: CalculatorResults, horizonYears: number): string {
  if (results.winner === 'even') return `It's a wash over ${horizonYears} years`
  const path = results.winner === 'buy' ? 'Buying' : 'Renting'
  return `${path} saves you ${fmtFullCurrency(results.difference)} over ${horizonYears} years`
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: 'green' | 'red' }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-b-0">
      <span className="text-body-sm text-neutral-dark">{label}</span>
      <span
        className={cn(
          'text-right text-body-sm-bold text-neutral-darker tabular-nums',
          highlight === 'green' && 'text-green',
          highlight === 'red' && 'text-red'
        )}
      >
        {value}
      </span>
    </div>
  )
}

export function ResultsPanel({ state, results }: { state: CalculatorState; results: CalculatorResults }) {
  const savingsPerMonth = Math.abs(results.difference) / state.horizonYears / 12
  const winnerColor = results.winner === 'buy' ? 'text-green' : results.winner === 'rent' ? 'text-red' : 'text-gold'

  return (
    <div className="flex flex-col gap-4">
      <div className="dark rounded-xl bg-card p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] tablet:p-8">
        <div className="grid items-center gap-4 tablet:grid-cols-[200px_1fr] tablet:gap-8">
          <GaugeSVG results={results} />
          <div>
            <p className={cn('mb-4 text-center text-body-bold tablet:text-left', winnerColor)}>
              {verdict(results, state.horizonYears)}
            </p>
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg border border-green-darker/25 bg-green-darker/[15%] p-3">
                <span className="mb-1 block text-subtitle-sm font-semibold text-green uppercase">
                  Total Cost to Buy
                </span>
                <span className="text-body-lg font-semibold text-white tabular-nums">
                  {fmtCurrency(results.totalBuyNetCost)}
                </span>
                <span className="block text-subtitle text-neutral-dark">
                  {monthlyCost(results.totalBuyNetCost, state.horizonYears)}
                </span>
              </div>
              <div className="flex-1 rounded-lg border border-foreground/[8%] bg-foreground/[6%] p-3">
                <span className="mb-1 block text-subtitle-sm font-semibold text-neutral-dark uppercase">
                  Total Cost to Rent
                </span>
                <span className="text-body-lg font-semibold text-white tabular-nums">
                  {fmtCurrency(results.totalRentNetCost)}
                </span>
                <span className="block text-subtitle text-neutral-dark">
                  {monthlyCost(results.totalRentNetCost, state.horizonYears)}
                </span>
              </div>
            </div>
            {results.winner !== 'even' && (
              <p
                className={cn(
                  'mt-4 border-t border-foreground/[8%] pt-3 text-center text-body-bold tabular-nums tablet:text-left',
                  winnerColor
                )}
              >
                That&apos;s {fmtFullCurrency(savingsPerMonth)}/mo in savings
              </p>
            )}
          </div>
        </div>
      </div>

      <AdCard winner={results.winner} />

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="mb-2 text-body-lg leading-snug font-semibold text-gray-darker">How it breaks down</p>
        <div className="grid gap-x-8 tablet:grid-cols-2">
          <div>
            <p className="pt-3 text-subtitle-sm font-bold tracking-widest text-green uppercase">Buying — Upfront</p>
            <Row label="Down payment" value={fmtCurrency(results.downPayment)} />
            <Row label="Closing costs" value={fmtCurrency(results.closingTotal)} />
            <Row label="Moving + inspection + repairs" value={fmtCurrency(results.moveInCosts)} />
            <p className="pt-5 text-subtitle-sm font-bold tracking-widest text-green uppercase">Buying — Ongoing</p>
            <Row label="Total mortgage payments" value={fmtCurrency(results.totalMortgagePaid)} />
            <Row label="Interest paid" value={fmtCurrency(results.totalInterestPaid)} highlight="red" />
            <Row
              label="Property tax + insurance"
              value={fmtCurrency(results.propertyTaxAndInsurance + results.totalPmi)}
            />
            <Row label="Maintenance + HOA + other" value={fmtCurrency(results.maintenanceHoaOther)} />
            <Row label="Utilities" value={fmtCurrency(results.totalUtilities)} />
            <Row label="Major repairs + upgrades" value={fmtCurrency(state.majorRepairs)} />
          </div>
          <div>
            <p className="pt-5 text-subtitle-sm font-bold tracking-widest text-green uppercase tablet:pt-3">
              Buying — Exit
            </p>
            <Row label="Home value at sale" value={fmtCurrency(results.endHomeValue)} highlight="green" />
            <Row label="Agent commission" value={fmtCurrency(results.agentCommission)} />
            <Row label="Capital gains tax" value={fmtCurrency(results.capitalGainsTax)} />
            <Row label="Staging + prep" value={fmtCurrency(state.stagingCost)} />
            <Row label="Net equity" value={fmtCurrency(results.netEquity)} highlight="green" />
            <p className="pt-5 text-subtitle-sm font-bold tracking-widest text-gold uppercase">Renting</p>
            <Row label="Total rent paid" value={fmtCurrency(results.totalRentPaid)} />
            <Row label="Renter's insurance" value={fmtCurrency(results.totalRentersInsurance)} />
            <Row
              label="Investment portfolio value"
              value={fmtCurrency(results.investmentPortfolio)}
              highlight="green"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
